// PROJECT · Mock - shared types + fixture data for the throwaway click-path
// prototype (grill-me session, 2026-09). Not wired to genesis; every value
// here is display fixture data, routed through react-intl like real copy so
// the prototype doesn't hide behind an @adr SPARK-0013-i18n-mandatory-via-react-intl@1 exception.
//
// Delete this whole `pages/project/` folder once the real feature is built
// from scratch - this is a discussion aid, not a starting point to extend.

export interface Msg {
  id: string;
  defaultMessage: string;
}

// Die Seite kennt zwei Zustände: anlegen, dann das Projekt selbst mit seinen Reitern.
// Die früheren Assistenten-Schritte waren nicht mehr erreichbar und sind entfernt (spark#1001).
export type ProjectMockStep = "create" | "overview";


// --- Facets: everything a Project can gain AFTER it exists.
//
// A Project is real as soon as it has a title. The six entries below are
// invitations, never preconditions - that is the whole difference to the
// wizard version of this prototype, where the same six things stood between
// the user and a Project that existed at all. The sidebar and the
// "Vervollständigen" box both render this one list, so there is a single
// place that decides what "complete" means.

export type ProjectFacetKey =
  | "anlass"
  | "aufteilen"
  | "team"
  | "mitglieder"
  | "dokumente"
  | "kandidaten"
  | "ablauf";

export interface ProjectFacet {
  key: ProjectFacetKey;
  label: Msg;
  /** Beschriftung der Pille, die den Block ins Projekt holt. */
  addLabel: Msg;
  /** Shown while the facet is missing - an offer, not a complaint. */
  empty: Msg;
  /** Filled-in stand-in once the facet is done (fixture, no real data). */
  filled: Msg;
  /**
   * Immer im Projekt, ohne dass man die Fläche erst holt. Für Flächen, die sich
   * nicht aus einer Entscheidung ergeben, sondern aus dem blossen Bestehen des
   * Projekts: die Aktivität gibt es, sobald es etwas zu berichten gibt, und die
   * Frage "willst du eine Aktivität haben" stellt sich niemand.
   */
  always?: boolean;
}

export const PROJECT_FACETS: readonly ProjectFacet[] = [
  {
    key: "anlass",
    label: { id: "project.facet.anlass.label", defaultMessage: "Anlass" },
    addLabel: { id: "project.facet.anlass.add", defaultMessage: "Anlass wählen" },
    empty: { id: "project.facet.anlass.empty", defaultMessage: "Worum geht es?" },
    filled: { id: "project.facet.anlass.filled", defaultMessage: "Programm · Rollout in drei Ländern" },
  },
  {
    // Heisst "Ablauf", nicht "Aufteilung": hier stehen Pakete, Aufgaben und
    // Termine in ihrer zeitlichen Ordnung. Aufteilen ist die Tätigkeit, der
    // Ablauf das Ergebnis - und danach sucht man später.
    key: "aufteilen",
    label: { id: "project.facet.aufteilen.label", defaultMessage: "Ablauf" },
    addLabel: { id: "project.facet.aufteilen.add", defaultMessage: "Ablauf planen" },
    empty: { id: "project.facet.aufteilen.empty", defaultMessage: "In Abschnitte zerlegen" },
    filled: { id: "project.facet.aufteilen.filled", defaultMessage: "4 Abschnitte" },
  },
  // ACHTUNG, Schlüssel und Beschriftung gehen hier auseinander, und das ist Absicht:
  // `team` heisst im UI "Anforderungen" (Fachkompetenzen + Powers, dieselbe Sprache wie
  // im Match), `kandidaten` heisst "Team" (die Menschen). Die Schlüssel bleiben,
  // weil sie in `data.sections` gespeicherter Bestand sind - sie umzubenennen
  // würde bei jedem vorhandenen Projekt eine Fläche verschwinden lassen.
  {
    key: "team",
    label: { id: "project.facet.team.label", defaultMessage: "Anforderungen" },
    addLabel: { id: "project.facet.team.add", defaultMessage: "Anforderungen hinzufügen" },
    empty: { id: "project.facet.team.empty", defaultMessage: "Welche Fähigkeiten braucht es?" },
    filled: { id: "project.facet.team.filled", defaultMessage: "Profil beschrieben" },
  },
  {
    // Eigene Fläche statt eines Abschnitts in der Übersicht: Unterlagen sind der
    // Stoff, aus dem später der Kontext für den Assistenten wird, und die
    // Übersicht soll kurz bleiben.
    key: "dokumente",
    label: { id: "project.facet.dokumente.label", defaultMessage: "Dokumente" },
    addLabel: { id: "project.facet.dokumente.add", defaultMessage: "Dokumente hinzufügen" },
    empty: { id: "project.facet.dokumente.empty", defaultMessage: "Noch nichts abgelegt" },
    filled: { id: "project.facet.dokumente.filled", defaultMessage: "Unterlagen abgelegt" },
  },
  {
    // Die Menschen SELBST stehen unter `mitglieder`; hier geht es darum, wie man
    // an sie kommt: Community, ein früheres Match, oder ein neues.
    key: "mitglieder",
    label: { id: "project.facet.mitglieder.label", defaultMessage: "Team" },
    addLabel: { id: "project.facet.mitglieder.add", defaultMessage: "Team anzeigen" },
    empty: { id: "project.facet.mitglieder.empty", defaultMessage: "Noch niemand dabei" },
    filled: { id: "project.facet.mitglieder.filled", defaultMessage: "Menschen im Projekt" },
  },
  {
    key: "kandidaten",
    label: { id: "project.facet.kandidaten.label", defaultMessage: "Match" },
    addLabel: { id: "project.facet.kandidaten.add", defaultMessage: "Menschen suchen" },
    empty: { id: "project.facet.kandidaten.empty", defaultMessage: "Wer könnte mitmachen?" },
    filled: { id: "project.facet.kandidaten.filled", defaultMessage: "Menschen gefragt" },
  },
  {
    // Die Fläche hiess "Ablauf" und zeigte dieselbe Zeitachse wie die Aufteilung
    // noch einmal. Sie ist jetzt die AKTIVITÄT: was im Projekt geschieht, nicht
    // was geplant ist. Der Plan lebt in der Aufteilung, hier steht sein Verlauf.
    key: "ablauf",
    always: true,
    label: { id: "project.facet.ablauf.label", defaultMessage: "Aktivität" },
    addLabel: { id: "project.facet.ablauf.add", defaultMessage: "Aktivität verfolgen" },
    empty: { id: "project.facet.ablauf.empty", defaultMessage: "Noch nichts passiert" },
    filled: { id: "project.facet.ablauf.filled", defaultMessage: "Läuft" },
  },
];

/** What lands in the activity feed when a facet is filled in. */
export const FACET_ACTIVITY: Record<ProjectFacetKey, Msg> = {
  anlass: { id: "project.activity.anlass", defaultMessage: "Anlass ergänzt" },
  aufteilen: { id: "project.activity.aufteilen", defaultMessage: "In Abschnitte aufgeteilt" },
  team: { id: "project.activity.team", defaultMessage: "Team beschrieben" },
  mitglieder: { id: "project.activity.mitglieder", defaultMessage: "Team ergänzt" },
  dokumente: { id: "project.activity.dokumente", defaultMessage: "Unterlagen abgelegt" },
  kandidaten: { id: "project.activity.kandidaten", defaultMessage: "Kandidaten ausgewählt" },
  ablauf: { id: "project.activity.ablauf", defaultMessage: "Ablauf gestartet" },
};

// --- Guided flow (Bernhard's SHARE-Neufassung-Mock, 4 Schritte: Anlass,
// Aufteilen, Einordnen, Team) - "Projekt" as an Anlass label is renamed to
// "Programm" here, see the grill-me naming-collision decision: the new
// Project entity is the canonical cross-repo term (@adr SPARK-0021-project-umbrella-with-projectdocument-cross-repo@1), the Anlass
// category needed a different word, not the same one.

export interface MockAnlassOption {
  id: string;
  title: Msg;
  desc: Msg;
}

export const MOCK_ANLASS_OPTIONS: readonly MockAnlassOption[] = [
  {
    id: "initiative",
    title: { id: "project.guided.anlass.initiative.title", defaultMessage: "Initiative" },
    desc: {
      id: "project.guided.anlass.initiative.desc",
      defaultMessage: "Etwas, das du anstoßen willst, ohne dass jemand es bestellt hat.",
    },
  },
  {
    id: "aufgabe",
    title: { id: "project.guided.anlass.aufgabe.title", defaultMessage: "Aufgabe" },
    desc: {
      id: "project.guided.anlass.aufgabe.desc",
      defaultMessage: "Etwas Abgegrenztes, das getan werden muss. Klein und schnell.",
    },
  },
  {
    id: "programm",
    title: { id: "project.guided.anlass.programm.title", defaultMessage: "Programm" },
    desc: {
      id: "project.guided.anlass.programm.desc",
      defaultMessage: "Etwas Größeres mit Etappen, das in Teile zerfällt.",
    },
  },
  {
    id: "auftrag",
    title: { id: "project.guided.anlass.auftrag.title", defaultMessage: "Auftrag" },
    desc: {
      id: "project.guided.anlass.auftrag.desc",
      defaultMessage: "Ein Kunde, ein vereinbartes Ergebnis, ein Preis.",
    },
  },
  {
    id: "ausschreibung",
    title: { id: "project.guided.anlass.ausschreibung.title", defaultMessage: "Ausschreibung" },
    desc: {
      id: "project.guided.anlass.ausschreibung.desc",
      defaultMessage: "Ein formales Verfahren mit Frist, Formvorgaben und Wertung.",
    },
  },
  {
    id: "planvorstellung",
    title: { id: "project.guided.anlass.planvorstellung.title", defaultMessage: "Planvorstellung" },
    desc: {
      id: "project.guided.anlass.planvorstellung.desc",
      defaultMessage: "Noch eine Idee, aber schon erzählbar. Zahlen sind geschätzt.",
    },
  },
];

export interface MockService {
  id: string;
  icon: string;
  // Brand-adjacent swatch colour, approximated, not the vendor's exact
  // official asset - see grill-me trademark discussion. Icon text is white
  // on all of these, chosen dark/saturated enough for contrast.
  color: string;
  title: Msg;
  desc: Msg;
}

export const MOCK_IMPORT_SERVICES: readonly MockService[] = [
  {
    id: "jira",
    icon: "JI",
    color: "#0052CC",
    title: { id: "project.data.service.jira.title", defaultMessage: "Jira" },
    desc: { id: "project.data.service.jira.desc", defaultMessage: "Tickets als Kontext" },
  },
  {
    id: "asana",
    icon: "AS",
    color: "#F06A6A",
    title: { id: "project.data.service.asana.title", defaultMessage: "Asana" },
    desc: { id: "project.data.service.asana.desc", defaultMessage: "Tasks als Kontext" },
  },
  {
    id: "monday",
    icon: "MO",
    color: "#FF3D57",
    title: { id: "project.data.service.monday.title", defaultMessage: "Monday" },
    desc: { id: "project.data.service.monday.desc", defaultMessage: "Boards als Kontext" },
  },
  {
    id: "trello",
    icon: "TR",
    color: "#0079BF",
    title: { id: "project.data.service.trello.title", defaultMessage: "Trello" },
    desc: { id: "project.data.service.trello.desc", defaultMessage: "Karten als Kontext" },
  },
];
